import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getHolesByCourse, calculateStablefordPoints } from './courseData';

function ScoreDonutChart({ distribution }) {
  const [chartView, setChartView] = useState('count');
  const [hovered, setHovered] = useState(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const CATEGORIES = [
    { key: 'eagle',  label: 'Eagle',   icon: '🦅', color: '#d4af37' },
    { key: 'birdie', label: 'Birdie',  icon: '🐦', color: '#10b981' },
    { key: 'par',    label: 'Par',     icon: '⚪', color: '#6b7280' },
    { key: 'bogey',  label: 'Bogey',   icon: '⚠️', color: '#f59e0b' },
    { key: 'double', label: 'Double',  icon: '❌', color: '#ef4444' },
    { key: 'triple', label: 'Triple+', icon: '💀', color: '#991b1b' },
  ];

  const total = distribution.total || 0;

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const rad = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const buildArcPath = (cx, cy, outerR, innerR, startAngle, endAngle) => {
    if (endAngle - startAngle >= 360) endAngle = startAngle + 359.9999;
    const os = polarToCartesian(cx, cy, outerR, startAngle);
    const oe = polarToCartesian(cx, cy, outerR, endAngle);
    const is_ = polarToCartesian(cx, cy, innerR, startAngle);
    const ie = polarToCartesian(cx, cy, innerR, endAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    const f = n => n.toFixed(3);
    return [
      `M ${f(os.x)} ${f(os.y)}`,
      `A ${outerR} ${outerR} 0 ${large} 1 ${f(oe.x)} ${f(oe.y)}`,
      `L ${f(ie.x)} ${f(ie.y)}`,
      `A ${innerR} ${innerR} 0 ${large} 0 ${f(is_.x)} ${f(is_.y)}`,
      'Z'
    ].join(' ');
  };

  const cx = 80, cy = 80, outerR = 64, innerR = 32;
  let angle = 0;
  const segments = CATEGORIES.map(cat => {
    const count = distribution[cat.key] || 0;
    const pct = total > 0 ? (count / total) * 100 : 0;
    const span = total > 0 ? (count / total) * 360 : 0;
    const start = angle;
    angle += span;
    return { ...cat, count, pct, path: count > 0 ? buildArcPath(cx, cy, outerR, innerR, start, angle) : null };
  });

  const toggleView = () => setChartView(v => v === 'count' ? 'percentage' : 'count');
  const hoveredCat = CATEGORIES.find(c => c.key === hovered);

  return (
    <div style={{ backgroundColor: '#0f0f0f', border: '1px solid #2d2d2d', borderRadius: '12px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>Score Distribution</div>
        <button onClick={toggleView} style={{
          padding: '4px 10px', backgroundColor: '#1e1e1e', border: '1px solid #2d2d2d',
          borderRadius: '20px', color: '#d4af37', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
        }}>
          {chartView === 'count' ? 'Counts' : 'Percentages'}
        </button>
      </div>

      {total === 0 ? (
        <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px 0', fontSize: '13px', fontStyle: 'italic' }}>
          No hole data available
        </p>
      ) : (
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <div style={{ flex: '0 0 45%' }}>
            <svg viewBox="0 0 160 160" style={{
              width: '100%', display: 'block', cursor: 'pointer',
              opacity: animated ? 1 : 0, transform: animated ? 'scale(1)' : 'scale(0.85)',
              transition: 'opacity 0.5s ease, transform 0.5s ease', transformOrigin: '50% 50%'
            }} onClick={toggleView}>
              <circle cx={cx} cy={cy} r={outerR} fill="#171717" />
              {segments.map(seg => seg.path && (
                <path key={seg.key} d={seg.path} fill={seg.color} style={{
                  opacity: hovered === null ? 0.9 : hovered === seg.key ? 1 : 0.3,
                  filter: hovered === seg.key ? 'brightness(1.4)' : 'none',
                  transition: 'opacity 0.2s ease, filter 0.2s ease',
                }}
                  onMouseEnter={() => setHovered(seg.key)}
                  onMouseLeave={() => setHovered(null)}
                />
              ))}
              <circle cx={cx} cy={cy} r={innerR} fill="#1e1e1e" />
              {hoveredCat ? (
                <>
                  <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
                    fill={hoveredCat.color} fontSize="8" fontFamily="system-ui, sans-serif" fontWeight="600">
                    {hoveredCat.icon} {hoveredCat.label}
                  </text>
                  <text x={cx} y={cy + 9} textAnchor="middle" dominantBaseline="middle"
                    fill="#ffffff" fontSize="14" fontFamily="system-ui, sans-serif" fontWeight="700">
                    {chartView === 'count' ? (distribution[hovered] || 0) : `${((distribution[hovered] || 0) / total * 100).toFixed(1)}%`}
                  </text>
                </>
              ) : (
                <>
                  <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
                    fill="#ffffff" fontSize="18" fontFamily="system-ui, sans-serif" fontWeight="700">
                    {total}
                  </text>
                  <text x={cx} y={cy + 10} textAnchor="middle" dominantBaseline="middle"
                    fill="#9ca3af" fontSize="7" fontFamily="system-ui, sans-serif">
                    holes
                  </text>
                </>
              )}
            </svg>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {segments.map(seg => {
              const isActive = hovered === seg.key;
              const val = chartView === 'count' ? seg.count : `${seg.pct.toFixed(1)}%`;
              return (
                <div key={seg.key} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 7px',
                  backgroundColor: isActive ? '#1a1a1a' : 'transparent',
                  borderRadius: '5px', border: `1px solid ${isActive ? seg.color + '60' : 'transparent'}`,
                  transition: 'all 0.15s ease', cursor: 'default'
                }}
                  onMouseEnter={() => setHovered(seg.key)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div style={{ width: '3px', height: '16px', borderRadius: '2px', backgroundColor: seg.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', flexShrink: 0 }}>{seg.icon}</span>
                  <span style={{ fontSize: '11px', flex: 1, color: isActive ? '#fff' : '#9ca3af', fontWeight: isActive ? '600' : '400' }}>
                    {seg.label}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: seg.color, fontVariantNumeric: 'tabular-nums', minWidth: '28px', textAlign: 'right' }}>
                    {val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const MiniChart = ({ allEvents, dataKey, color, label, minScale, maxScale }) => {
  if (!allEvents || allEvents.length < 2) return (
    <div style={{padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px'}}>
      Not enough events for trend
    </div>
  );

  const fieldAvgKey = dataKey === 'points' ? 'fieldAvgPoints' : 'fieldAvgGross';

  const displayMin = minScale !== undefined ? minScale : 0;
  const displayMax = maxScale !== undefined ? maxScale : 40;
  const displayRange = displayMax - displayMin;

  const playerValues = allEvents
    .filter(e => e.participated && e[dataKey] !== null)
    .map(e => e[dataKey]);

  const playerAverage = playerValues.length > 0
    ? playerValues.reduce((sum, v) => sum + v, 0) / playerValues.length
    : 0;

  const chartHeight = 100;
  const chartWidth = 100;
  const barWidth = chartWidth / allEvents.length;
  const padding = barWidth * 0.2;

  return (
    <div style={{padding: '10px 0'}}>
      <div style={{fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500'}}>{label}</div>
      <svg viewBox={`0 0 ${chartWidth + 15} ${chartHeight + 30}`} style={{width: '100%', height: '150px'}}>
        {/* Y-axis label */}
        <text
          x="-50"
          y="3"
          transform="rotate(-90)"
          textAnchor="middle"
          fontSize="8"
          fill="#9ca3af"
          fontWeight="600"
        >
          {label}
        </text>

        {/* Y-axis scale markers */}
        <text x="3" y="10" fontSize="7" fill="#6b7280" textAnchor="end">{displayMax}</text>
        <text x="3" y={chartHeight/2 + 3} fontSize="7" fill="#6b7280" textAnchor="end">
          {Math.round((displayMax + displayMin) / 2)}
        </text>
        <text x="3" y={chartHeight - 2} fontSize="7" fill="#6b7280" textAnchor="end">{displayMin}</text>

        {/* Grid lines */}
        <line x1="8" y1={chartHeight/2} x2={chartWidth + 8} y2={chartHeight/2}
          stroke="#2d2d2d" strokeWidth="0.5" strokeDasharray="2,2"/>
        <line x1="8" y1={chartHeight} x2={chartWidth + 8} y2={chartHeight}
          stroke="#2d2d2d" strokeWidth="1"/>

        {/* Bars and X-axis labels — one slot per event */}
        {allEvents.map((event, i) => {
          const val = event[dataKey];
          const barHeight = event.participated && val !== null
            ? Math.max(((val - displayMin) / displayRange) * chartHeight, 0)
            : 0;
          const barY = chartHeight - barHeight;
          const centerX = 8 + i * barWidth + barWidth / 2;
          const x = 8 + i * barWidth + padding;
          const d = event.date ? new Date(event.date) : null;
          const dateLabel = d ? `${d.getMonth() + 1}/${d.getDate()}` : `E${event.eventNumber}`;

          return (
            <g key={i}>
              {event.participated && val !== null && (
                <>
                  <rect
                    x={x}
                    y={barY}
                    width={barWidth - (padding * 2)}
                    height={Math.max(barHeight, 2)}
                    fill={color}
                    opacity="0.8"
                  />
                  <text
                    x={centerX}
                    y={barY - 3}
                    textAnchor="middle"
                    fontSize="7"
                    fill="#ffffff"
                    fontWeight="700"
                  >
                    {val}
                  </text>
                </>
              )}
              {/* X-axis label — dimmed for missed events */}
              <text
                x={centerX}
                y={chartHeight + 10}
                textAnchor="middle"
                fontSize="6"
                fill={event.participated ? "#9ca3af" : "#4b5563"}
                fontWeight="500"
              >
                {dateLabel}
              </text>
            </g>
          );
        })}

        {/* Field average trendline — continuous across ALL events */}
        {allEvents.map((event, i) => {
          if (i === allEvents.length - 1) return null;
          const avg = event[fieldAvgKey];
          const nextAvg = allEvents[i + 1][fieldAvgKey];
          if (!avg || !nextAvg) return null;

          const x1 = 8 + i * barWidth + barWidth / 2;
          const y1 = chartHeight - ((avg - displayMin) / displayRange) * chartHeight;
          const x2 = 8 + (i + 1) * barWidth + barWidth / 2;
          const y2 = chartHeight - ((nextAvg - displayMin) / displayRange) * chartHeight;

          return (
            <line
              key={`line-${i}`}
              x1={x1} y1={y1}
              x2={x2} y2={y2}
              stroke="#d4af37"
              strokeWidth="2"
            />
          );
        })}

        {/* Field average dots for ALL events */}
        {allEvents.map((event, i) => {
          const avg = event[fieldAvgKey];
          if (!avg) return null;

          const x = 8 + i * barWidth + barWidth / 2;
          const y = chartHeight - ((avg - displayMin) / displayRange) * chartHeight;

          return (
            <g key={`dot-${i}`}>
              <circle cx={x} cy={y} r="2.5" fill="#d4af37" stroke="#0f0f0f" strokeWidth="1" />
              <text
                x={x}
                y={y - 6}
                textAnchor="middle"
                fontSize="6"
                fill="#d4af37"
                fontWeight="600"
              >
                {avg.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* X-axis label */}
        <text
          x={(chartWidth + 15) / 2}
          y={chartHeight + 25}
          textAnchor="middle"
          fontSize="8"
          fill="#9ca3af"
          fontWeight="600"
        >
          Events
        </text>
      </svg>
      <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginTop: '8px'}}>
        <span>Min: <strong style={{color: '#ef4444'}}>{playerValues.length > 0 ? Math.min(...playerValues) : '-'}</strong></span>
        <span>Player Avg: <strong style={{color: color}}>{playerAverage.toFixed(1)}</strong></span>
        <span>Max: <strong style={{color: '#10b981'}}>{playerValues.length > 0 ? Math.max(...playerValues) : '-'}</strong></span>
      </div>
    </div>
  );
};

function PlayerProfile({ userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const isMobile = window.innerWidth < 480;

  useEffect(() => {
  const loadPlayerData = async () => {
    try {
      setLoading(true);

      // Load user profile
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      if (!userDoc.empty) {
        setUser(userDoc.docs[0].data());
      }

      // Load all users for partner name lookups
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const usersMap = {};
      allUsersSnapshot.docs.forEach(doc => {
        usersMap[doc.id] = doc.data();
      });
      setUsersMap(usersMap);

      // Load player's rounds
      const roundsQuery = query(
        collection(db, 'rounds'),
        where('userId', '==', userId),
        where('status', '==', 'approved')
      );

      const roundsSnapshot = await getDocs(roundsQuery);
      
      if (roundsSnapshot.empty) {
        setStats(null);
        setLoading(false);
        return;
      }

      // Calculate field averages for each event/date
      const fieldAverages = {};
      
      // Get all approved rounds to calculate field averages
      const allRoundsQuery = query(
        collection(db, 'rounds'),
        where('status', '==', 'approved')
      );
      const allRoundsSnapshot = await getDocs(allRoundsQuery);
      
      // Group rounds by event/date
      const roundsByEvent = {};
      allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const round = doc.data();
        const key = `${round.eventId || round.date}`;
        if (!roundsByEvent[key]) {
          roundsByEvent[key] = [];
        }
        roundsByEvent[key].push(round);
      });
      
      // Calculate averages
      Object.keys(roundsByEvent).forEach(key => {
        const rounds = roundsByEvent[key];
        const totalPoints = rounds.reduce((sum, r) => sum + (r.totalStableford || 0), 0);
        const totalGross = rounds.reduce((sum, r) => sum + (r.totalGross || 0), 0);
        fieldAverages[key] = {
          avgPoints: totalPoints / rounds.length,
          avgGross: totalGross / rounds.length
        };
      });

      // Build field avg putts per event for SGP
      const puttsByEvent = {};
      allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const r = doc.data();
        if (!r.eventId || !r.totalPutts) return;
        if (!puttsByEvent[r.eventId]) puttsByEvent[r.eventId] = { total: 0, count: 0 };
        puttsByEvent[r.eventId].total += r.totalPutts;
        puttsByEvent[r.eventId].count++;
      });
      const fieldAvgPutts = {};
      Object.entries(puttsByEvent).forEach(([eid, val]) => {
        fieldAvgPutts[eid] = val.total / val.count;
      });

      // Continue with existing stats calculation...

      // Build player's round lookup by event key
      const playerRoundsByKey = {};
      roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const round = doc.data();
        const key = `${round.eventId || round.date}`;
        playerRoundsByKey[key] = round;
      });

      // Capture a representative date for each event key
      const eventDates = {};
      allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const round = doc.data();
        const key = `${round.eventId || round.date}`;
        if (!eventDates[key]) eventDates[key] = round.date;
      });

      // Build sorted all-events array (includes events the player missed)
      const allEvents = Object.keys(roundsByEvent)
        .map(key => {
          const playerRound = playerRoundsByKey[key];
          const fa = fieldAverages[key];
          return {
            key,
            date: eventDates[key],
            participated: !!playerRound,
            points: playerRound ? (playerRound.totalStableford ?? null) : null,
            gross: playerRound ? (playerRound.totalGross ?? null) : null,
            fieldAvgPoints: fa?.avgPoints ?? null,
            fieldAvgGross: fa?.avgGross ?? null,
          };
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((ev, i) => ({ ...ev, eventNumber: i + 1 }));

        // Calculate comprehensive stats
        const scoreDistribution = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0, triple: 0, total: 0 };

        let totalRounds = 0;
        let totalPoints = 0;
        let totalGross = 0;
        let totalPutts = 0;
        let totalGIR = 0;
        let totalFairways = 0;
        let totalFairwayOpps = 0;
        let totalUpDowns = 0;
        let totalUpDownOpps = 0;
        let totalCigarettes = 0;
        let totalBorrowed = 0;
        let totalDrinks = 0;
        let bestRound = 0;
        let worstRound = 999;
        let courses = { 'De Bleekloop': 0, 'De Plantage': 0 };
        let recentRounds = [];
        const parStats = { 3: { total: 0, count: 0 }, 4: { total: 0, count: 0 }, 5: { total: 0, count: 0 } };

        roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
          const round = doc.data();
          totalRounds++;
          totalPoints += round.totalStableford || 0;
          totalGross += round.totalGross || 0;
          totalPutts += round.totalPutts || 0;
          totalGIR += round.greensInRegulation || 0;
          totalFairways += round.fairwaysHit || 0;
          totalFairwayOpps += round.fairwaysTotal || 0;
          totalUpDowns += round.upAndDowns || 0;
          totalUpDownOpps += round.upAndDownOpportunities || 0;
          // Calculate scrambling (missed GIR but made par or better)
if (round.holeData) {
  const holes = getHolesByCourse(round.course);
  holes.forEach(hole => {
    const data = round.holeData[hole.number];
    if (data && data.strokes > 0) {
      // Track strokes by par type
      if (parStats[hole.par]) {
        parStats[hole.par].total += data.strokes;
        parStats[hole.par].count++;
      }
      // Score distribution
      const diff = data.strokes - hole.par;
      scoreDistribution.total++;
      if (diff <= -2) scoreDistribution.eagle++;
      else if (diff === -1) scoreDistribution.birdie++;
      else if (diff === 0) scoreDistribution.par++;
      else if (diff === 1) scoreDistribution.bogey++;
      else if (diff === 2) scoreDistribution.double++;
      else scoreDistribution.triple++;
    }
  });
}
          totalCigarettes += round.cigarettesSmoked || 0;
          totalBorrowed += round.cigarettesBorrowed || 0;
          totalDrinks += round.beersAfterRound || 0;

          if (round.totalStableford > bestRound) bestRound = round.totalStableford;
          if (round.totalStableford < worstRound) worstRound = round.totalStableford;

          if (courses[round.course] !== undefined) {
            courses[round.course]++;
          }

          recentRounds.push({
  date: round.date,
  eventId: round.eventId,
  course: round.course,
  points: round.totalStableford,
  gross: round.totalGross,
  putts: round.totalPutts,
  sgp: round.eventId && fieldAvgPutts[round.eventId] && round.totalPutts
    ? parseFloat((fieldAvgPutts[round.eventId] - round.totalPutts).toFixed(2))
    : null,
});
        });

        // Sort recent rounds by date
        recentRounds.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Compute season avg SGP
        let totalSGP = 0;
        let sgpCount = 0;
        roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
          const r = doc.data();
          if (r.eventId && fieldAvgPutts[r.eventId] && r.totalPutts) {
            totalSGP += fieldAvgPutts[r.eventId] - r.totalPutts;
            sgpCount++;
          }
        });
        const seasonSGP = sgpCount > 0
          ? parseFloat((totalSGP / sgpCount).toFixed(2))
          : null;

// Form: last 5 vs season average
const last5 = recentRounds.slice(0, 5);
const last5Avg = last5.length > 0 
  ? Math.round(last5.reduce((sum, r) => sum + r.points, 0) / last5.length) 
  : 0;
const seasonAvg = Math.round(totalPoints / totalRounds);
const formDiff = last5Avg - seasonAvg;
const formStatus = formDiff >= 2 ? 'hot' : formDiff <= -2 ? 'cold' : 'steady';

// Chronological rounds for trend chart (oldest first)
const chronologicalRounds = [...recentRounds].reverse();

// Partner analysis
const getUserName = (uid) => usersMap[uid]?.name || 'Unknown';
const partnerMap = {};
roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
  const round = doc.data();
  const members = round.flightMembers;
  if (!members || !Array.isArray(members) || members.length === 0) return;
  members.forEach(partnerId => {
    if (partnerId === userId) return;
    if (!partnerMap[partnerId]) {
      partnerMap[partnerId] = { rounds: 0, totalStableford: 0, totalGross: 0, totalPutts: 0, totalUpDowns: 0, totalUpDownOpps: 0 };
    }
    partnerMap[partnerId].rounds++;
    partnerMap[partnerId].totalStableford += round.totalStableford || 0;
    partnerMap[partnerId].totalGross += round.totalGross || 0;
    partnerMap[partnerId].totalPutts += round.totalPutts || 0;
    partnerMap[partnerId].totalUpDowns += round.upAndDowns || 0;
    partnerMap[partnerId].totalUpDownOpps += round.upAndDownOpportunities || 0;
  });
});
const partnerStats = Object.entries(partnerMap)
  .map(([partnerId, data]) => ({
    partnerId,
    name: getUserName(partnerId),
    rounds: data.rounds,
    avgPoints: (data.totalStableford / data.rounds).toFixed(1),
    avgGross: (data.totalGross / data.rounds).toFixed(1),
    avgPutts: (data.totalPutts / data.rounds).toFixed(1),
    upDownPct: data.totalUpDownOpps > 0 ? Math.round((data.totalUpDowns / data.totalUpDownOpps) * 100) : 0,
  }))
  .sort((a, b) => parseFloat(b.avgPoints) - parseFloat(a.avgPoints));

        const parTypeStats = {
          par3: parStats[3].count > 0 ? parStats[3].total / parStats[3].count : null,
          par4: parStats[4].count > 0 ? parStats[4].total / parStats[4].count : null,
          par5: parStats[5].count > 0 ? parStats[5].total / parStats[5].count : null,
          par3Count: parStats[3].count,
          par4Count: parStats[4].count,
          par5Count: parStats[5].count,
        };

        const calculatedStats = {
  totalRounds,
  fieldAverages,
  avgPoints: Math.round(totalPoints / totalRounds),
  avgGross: Math.round(totalGross / totalRounds),
  avgPutts: (totalPutts / totalRounds).toFixed(1),
  girPercentage: totalRounds > 0 ? Math.round((totalGIR / (totalRounds * 9)) * 100) : 0,
  fairwayPercentage: totalFairwayOpps > 0 ? Math.round((totalFairways / totalFairwayOpps) * 100) : 0,
  upDownPercentage: totalUpDownOpps > 0 ? Math.round((totalUpDowns / totalUpDownOpps) * 100) : 0,
  seasonSGP,
  avgCigarettes: (totalCigarettes / totalRounds).toFixed(1),
  avgBorrowed: (totalBorrowed / totalRounds).toFixed(1),
  avgDrinks: (totalDrinks / totalRounds).toFixed(1),
  bestRound,
  worstRound: worstRound === 999 ? 0 : worstRound,
  favoriteCourse: courses['De Bleekloop'] > courses['De Plantage'] ? 'De Bleekloop' : 'De Plantage',
  courseBreakdown: courses,
  recentRounds: recentRounds.slice(0, 5),
  chronologicalRounds,
  allEvents,
  formStatus,
  last5Avg,
  formDiff,
  seasonAvg,
  totalStats: {
    totalGIR,
    totalFairways,
    totalUpDowns,
    totalCigarettes,
    totalBorrowed,
    totalDrinks
  },
  parTypeStats,
  scoreDistribution,
  partnerStats,
};

        setStats(calculatedStats);
      } catch (error) {
        console.error('Error loading player data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlayerData();
  }, [userId]);

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Loading...</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>No Data Available</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
          <div style={styles.content}>
            <p style={styles.emptyState}>This player hasn't completed any rounds yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.cardLarge}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{user?.name || 'Player Profile'}</h2>
            <p style={styles.subtitle}>{stats.totalRounds} rounds played</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.content}>
            {/* Current Form */}
<div style={styles.section}>
  <h3 style={styles.sectionTitle}>
    {stats.formStatus === 'hot' ? '🔥' : stats.formStatus === 'cold' ? '🥶' : '➡️'} Current Form
  </h3>
  <div style={styles.formCard}>
    <div style={styles.formBadge(stats.formStatus)}>
      {stats.formStatus === 'hot' ? '🔥 HOT' : stats.formStatus === 'cold' ? '🥶 COLD' : '➡️ STEADY'}
    </div>
    <div style={styles.formStats}>
      <div style={styles.formStat}>
        <div style={styles.formStatLabel}>Last 5 Avg</div>
        <div style={styles.formStatValue}>{stats.last5Avg}</div>
      </div>
      <div style={styles.formStatDivider}>vs</div>
      <div style={styles.formStat}>
        <div style={styles.formStatLabel}>Season Avg</div>
        <div style={styles.formStatValue}>{stats.seasonAvg}</div>
      </div>
      <div style={styles.formStat}>
        <div style={styles.formStatLabel}>Difference</div>
        <div style={{
          ...styles.formStatValue,
          color: stats.formDiff > 0 ? '#48bb78' : stats.formDiff < 0 ? '#e53e3e' : '#718096'
        }}>
          {stats.formDiff > 0 ? '+' : ''}{stats.formDiff}
        </div>
      </div>
    </div>
  </div>
</div>

{/* Trends */}
<div style={styles.section}>
  <h3 style={styles.sectionTitle}>📈 Trends Over Time</h3>
  <div style={styles.chartsGrid}>
    <div style={styles.chartCard}>
      <div style={styles.chartTitle}>📊 Stableford Points</div>
      
      <MiniChart
  allEvents={stats.allEvents}
  dataKey="points"
  color="#d4af37"
  label="Points"
  minScale={0}
  maxScale={40}
/>
      <div style={styles.chartStat}>
        <span>Best: <strong>{stats.bestRound}</strong></span>
        <span>Avg: <strong>{stats.avgPoints}</strong></span>
        <span>Worst: <strong>{stats.worstRound}</strong></span>
      </div>
    </div>
    <div style={styles.chartCard}>
      <div style={styles.chartTitle}>🏌️ Gross Score</div>
      <MiniChart
  allEvents={stats.allEvents}
  dataKey="gross"
  color="#3b82f6"
  label="Strokes"
  minScale={20}
  maxScale={70}
/>
      <div style={styles.chartStat}>
        <span>Best: <strong>{Math.min(...stats.chronologicalRounds.map(r => r.gross))}</strong></span>
        <span>Avg: <strong>{stats.avgGross}</strong></span>
        <span>Worst: <strong>{Math.max(...stats.chronologicalRounds.map(r => r.gross))}</strong></span>
      </div>
    </div>
  </div>
</div>

{/* Personal Records */}
<div style={styles.section}>
  <h3 style={styles.sectionTitle}>🏅 Personal Records</h3>
  <div style={styles.recordsGrid}>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>🥇</div>
      <div style={styles.recordLabel}>Best Round Ever</div>
      <div style={styles.recordValue}>{stats.bestRound} pts</div>
    </div>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>📊</div>
      <div style={styles.recordLabel}>Season Average</div>
      <div style={styles.recordValue}>{stats.avgPoints} pts</div>
    </div>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>🏌️</div>
      <div style={styles.recordLabel}>Best Gross</div>
      <div style={styles.recordValue}>{Math.min(...stats.chronologicalRounds.map(r => r.gross))} strokes</div>
    </div>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>🎯</div>
      <div style={styles.recordLabel}>Best GIR %</div>
      <div style={styles.recordValue}>{stats.girPercentage}%</div>
    </div>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>⛳</div>
      <div style={styles.recordLabel}>Total Rounds</div>
      <div style={styles.recordValue}>{stats.totalRounds}</div>
    </div>
    <div style={styles.recordCard}>
      <div style={styles.recordIcon}>🏆</div>
      <div style={styles.recordLabel}>Fav Course</div>
      <div style={{...styles.recordValue, fontSize: '14px'}}>{stats.favoriteCourse}</div>
    </div>
  </div>
</div>
          {/* Performance Overview */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>⛳ Performance Overview</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Points</div>
                <div style={styles.statValue}>{stats.avgPoints}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Best Round</div>
                <div style={styles.statValue}>{stats.bestRound}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Gross</div>
                <div style={styles.statValue}>{stats.avgGross}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Putts</div>
                <div style={styles.statValue}>{stats.avgPutts}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>SGP Avg</div>
                <div style={{
                  ...styles.statValue,
                  color: stats.seasonSGP > 0 ? '#10b981'
                       : stats.seasonSGP < 0 ? '#ef4444'
                       : '#d4af37',
                  fontSize: '28px'
                }}>
                  {stats.seasonSGP !== null
                    ? (stats.seasonSGP > 0 ? '+' : '') + stats.seasonSGP
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Shot Quality */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎯 Shot Quality</h3>
            <div style={styles.progressBars}>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Greens in Regulation</span>
                  <span style={styles.progressValue}>{stats.girPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.girPercentage}%`}} />
                </div>
              </div>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Fairway Accuracy</span>
                  <span style={styles.progressValue}>{stats.fairwayPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.fairwayPercentage}%`}} />
                </div>
              </div>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Up & Down</span>
                  <span style={styles.progressValue}>{stats.upDownPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.upDownPercentage}%`}} />
                </div>
              </div>
            </div>
          </div>

          {/* Fun Stats */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎉 Fun Stats</h3>
            <div style={styles.funStatsGrid}>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🚬</div>
                <div style={styles.funStatValue}>{stats.totalStats.totalCigarettes}</div>
                <div style={styles.funStatLabel}>Cigarettes Smoked</div>
                <div style={styles.funStatSub}>{stats.avgCigarettes}/round</div>
              </div>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🚬</div>
                <div style={styles.funStatValue}>{stats.totalStats.totalBorrowed}</div>
                <div style={styles.funStatLabel}>Cigarettes Borrowed</div>
                <div style={styles.funStatSub}>{stats.avgBorrowed}/round</div>
              </div>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🍺</div>
                <div style={styles.funStatValue}>{stats.totalStats.totalDrinks}</div>
                <div style={styles.funStatLabel}>Drinks Consumed</div>
                <div style={styles.funStatSub}>{stats.avgDrinks}/round</div>
              </div>
            </div>
          </div>

          {/* Score by Par Type */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>⛳ Average Par Score</h3>
            <div style={{ ...styles.statsGrid, gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { par: 3, icon: '3️⃣', avg: stats.parTypeStats.par3, count: stats.parTypeStats.par3Count },
                { par: 4, icon: '4️⃣', avg: stats.parTypeStats.par4, count: stats.parTypeStats.par4Count },
                { par: 5, icon: '5️⃣', avg: stats.parTypeStats.par5, count: stats.parTypeStats.par5Count },
              ].map(({ par, icon, avg }) => {
                const color = avg === null
                  ? '#6b7280'
                  : avg < par ? '#10b981'
                  : avg <= par + 1 ? '#d4af37'
                  : '#ef4444';
                return (
                  <div key={par} style={styles.statCard}>
                    <div style={styles.statLabel}>{icon} Par {par}</div>
                    <div style={{ ...styles.statValue, color, fontVariantNumeric: 'tabular-nums' }}>
                      {avg !== null ? avg.toFixed(1) : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Score Distribution */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📊 Score Distribution</h3>
            <ScoreDonutChart distribution={stats.scoreDistribution} />
          </div>

          {/* Playing Partners */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🤝 Best Playing Partners</h3>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '-15px', marginBottom: '20px', fontWeight: '400' }}>
              Who brings out the best in {user?.name?.split(' ')[0] || 'this player'}?
            </p>
            {stats.partnerStats.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                No partner data available yet
              </p>
            ) : (
              <>
                {/* Top partner highlight */}
                <div style={styles.partnerHighlight}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#d4af37' }}>
                    🏆 Best Partner: {stats.partnerStats[0].name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px' }}>
                    Averages {stats.partnerStats[0].avgPoints} pts together over {stats.partnerStats[0].rounds} round{stats.partnerStats[0].rounds !== 1 ? 's' : ''}
                  </div>
                </div>
                {/* Partner cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {stats.partnerStats.map(partner => (
                    <div key={partner.partnerId} style={{
                      backgroundColor: '#0f0f0f',
                      border: '1px solid #2d2d2d',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', marginBottom: '2px' }}>
                          {partner.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {partner.rounds} round{partner.rounds !== 1 ? 's' : ''} together
                        </div>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                      }}>
                        {[
                          { label: 'AVG PTS', value: partner.avgPoints, color: '#d4af37' },
                          { label: 'AVG GROSS', value: partner.avgGross, color: '#9ca3af' },
                          { label: 'AVG PUTTS', value: partner.avgPutts, color: '#3b82f6' },
                          { label: 'UP & DOWN', value: partner.upDownPct + '%', color: '#10b981' },
                        ].map(pill => (
                          <div key={pill.label} style={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #2d2d2d',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            textAlign: 'center',
                          }}>
                            <div style={{
                              fontSize: '10px', fontWeight: '700', color: '#6b7280',
                              textTransform: 'uppercase', letterSpacing: '0.05em',
                              marginBottom: '4px',
                            }}>
                              {pill.label}
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: pill.color }}>
                              {pill.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Course Breakdown */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🏌️ Course Breakdown</h3>
            <div style={styles.courseBreakdown}>
              <div style={styles.courseItem}>
                <span style={styles.courseName}>De Bleekloop</span>
                <span style={styles.courseRounds}>{stats.courseBreakdown['De Bleekloop']} rounds</span>
              </div>
              <div style={styles.courseItem}>
                <span style={styles.courseName}>De Plantage</span>
                <span style={styles.courseRounds}>{stats.courseBreakdown['De Plantage']} rounds</span>
              </div>
              <div style={styles.favoriteNote}>
                ⭐ Favorite: {stats.favoriteCourse}
              </div>
            </div>
          </div>

          {/* Recent Rounds */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📅 Recent Rounds</h3>
            <div style={styles.recentRounds}>
              {stats.recentRounds.map((round, index) => (
                <div key={index} style={{
                  ...styles.recentRoundCard,
                  gridTemplateColumns: isMobile ? '60px 1fr 70px 60px' : '70px 1fr 80px 90px 70px',
                }}>
                  <div style={styles.recentRoundDate}>
                    {new Date(round.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div style={styles.recentRoundCourse}>{round.course}</div>
                  <div style={styles.recentRoundPoints}>
                    <strong>{round.points}</strong> pts
                  </div>
                  {!isMobile && (
                    <div style={styles.recentRoundGross}>
                      {round.gross} strokes
                    </div>
                  )}
                  <div style={{
                    fontSize: '13px',
                    color: round.sgp > 0 ? '#10b981' : round.sgp < 0 ? '#ef4444' : '#9ca3af',
                    textAlign: 'right',
                    fontWeight: '600'
                  }}>
                    {round.sgp !== null
                      ? (round.sgp > 0 ? '+' : '') + round.sgp
                      : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  cardLarge: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0,
    background: '#1e1e1e',
    borderRadius: '12px 12px 0 0'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#d4af37',
    margin: 0,
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '5px 0 0 0',
    fontWeight: '400'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: '1px solid #d4af37',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#d4af37',
    padding: '5px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  section: {
    marginBottom: '35px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #2d2d2d',
    letterSpacing: '-0.01em'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px'
  },
  statCard: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #2d2d2d'
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '8px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  progressBars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    fontWeight: '600',
    color: '#ffffff'
  },
  progressValue: {
    color: '#d4af37',
    fontWeight: '700'
  },
  progressBar: {
    width: '100%',
    height: '12px',
    backgroundColor: '#2d2d2d',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#d4af37',
    transition: 'width 0.3s ease'
  },
  funStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '20px'
  },
  funStatCard: {
    backgroundColor: '#0f0f0f',
    color: 'white',
    padding: '25px',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #d4af37'
  },
  funStatIcon: {
    fontSize: '36px',
    marginBottom: '10px'
  },
  funStatValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#d4af37',
    marginBottom: '5px',
    letterSpacing: '-0.02em'
  },
  funStatLabel: {
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '5px',
    fontWeight: '400'
  },
  funStatSub: {
    fontSize: '12px',
    color: '#d4af37',
    opacity: 0.8,
    fontWeight: '500'
  },
  courseBreakdown: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  courseItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #2d2d2d'
  },
  courseName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  courseRounds: {
    fontSize: '15px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  favoriteNote: {
    marginTop: '15px',
    padding: '12px',
    backgroundColor: '#1a1a1a',
    borderRadius: '6px',
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: '600',
    color: '#d4af37',
    border: '1px solid #2d2d2d'
  },
  recentRounds: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  recentRoundCard: {
    display: 'grid',
    gridTemplateColumns: '70px 1fr 80px 90px',
    gap: '10px',
    padding: '12px',
    backgroundColor: '#0f0f0f',
    borderRadius: '8px',
    alignItems: 'center',
    border: '1px solid #2d2d2d'
  },
  recentRoundDate: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff'
  },
  recentRoundCourse: {
    fontSize: '14px',
    color: '#9ca3af',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: '500'
  },
  recentRoundPoints: {
    fontSize: '16px',
    color: '#d4af37',
    textAlign: 'right',
    fontWeight: '600'
  },
  recentRoundGross: {
    fontSize: '14px',
    color: '#9ca3af',
    textAlign: 'right',
    fontWeight: '500'
  },
  formCard: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center'
  },
  formBadge: (status) => ({
    padding: '10px 30px',
    borderRadius: '30px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: status === 'hot' ? '#1a1a1a' : status === 'cold' ? '#1a1a1a' : '#0f0f0f',
    color: status === 'hot' ? '#ef4444' : status === 'cold' ? '#3b82f6' : '#9ca3af',
    border: `1px solid ${status === 'hot' ? '#ef4444' : status === 'cold' ? '#3b82f6' : '#2d2d2d'}`
  }),
  formStats: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  formStat: {
    textAlign: 'center'
  },
  formStatLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    marginBottom: '5px',
    letterSpacing: '0.05em'
  },
  formStatValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: '-0.02em'
  },
  formStatDivider: {
    fontSize: '16px',
    color: '#9ca3af',
    fontWeight: '600'
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  chartCard: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '12px',
    padding: '20px'
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '10px',
    letterSpacing: '-0.01em'
  },
  chartStat: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#9ca3af',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #2d2d2d',
    fontWeight: '500'
  },
  recordsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '15px'
  },
  recordCard: {
    backgroundColor: '#0f0f0f',
    border: '2px solid #d4af37',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center'
  },
  recordIcon: {
    fontSize: '28px',
    marginBottom: '8px'
  },
  recordLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    marginBottom: '8px',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  recordValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.01em'
  },
  partnerHighlight: {
    backgroundColor: 'rgba(212,175,55,0.08)',
    border: '1px solid rgba(212,175,55,0.3)',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  partnerPill: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    padding: '6px 12px',
    textAlign: 'center',
  },
  partnerPillLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  partnerPillValue: {
    fontSize: '16px',
    fontWeight: '700',
  },
};

export default PlayerProfile;